type EventDetail = {
  label: string;
  value: string;
};

type RegistrationStatusPanelProps = {
  variant: "loading" | "success" | "error";
  title: string;
  message: string;
  detail?: string;
  eventDetails?: EventDetail[];
  primaryAction?: {
    label: string;
    onClick: () => void;
  };
  secondaryAction?: {
    label: string;
    onClick: () => void;
  };
};

const StatusIcon = ({ variant }: { variant: RegistrationStatusPanelProps["variant"] }) => {
  if (variant === "loading") {
    return (
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-neutral">
        <div className="spinner h-8 w-8" />
      </div>
    );
  }

  if (variant === "success") {
    return (
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-success/20 text-success">
        <svg
          aria-hidden="true"
          className="h-8 w-8"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2.5}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      </div>
    );
  }

  return (
    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-error/20 text-error">
      <svg
        aria-hidden="true"
        className="h-8 w-8"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2.5}
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M12 3a9 9 0 100 18 9 9 0 000-18z" />
      </svg>
    </div>
  );
};

export default function RegistrationStatusPanel({
  variant,
  title,
  message,
  detail,
  eventDetails,
  primaryAction,
  secondaryAction,
}: RegistrationStatusPanelProps) {
  return (
    <div className="flex min-h-[420px] w-full flex-col items-center justify-center px-6 py-12 text-center">
      <StatusIcon variant={variant} />
      <h2 className="mt-6 text-2xl font-bold text-primary">{title}</h2>
      <p className="mt-3 max-w-md text-base leading-relaxed text-gray-700">{message}</p>
      {eventDetails && eventDetails.length > 0 && (
        <div className="mt-6 w-full max-w-md overflow-hidden rounded-xl border border-primary/10 bg-neutral text-left shadow-sm">
          <div className="border-b border-primary/10 bg-white px-5 py-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-secondary">
              Event details
            </p>
          </div>
          <dl className="divide-y divide-primary/10">
            {eventDetails.map((item) => (
              <div key={item.label} className="grid gap-1 px-5 py-4 sm:grid-cols-[88px_1fr] sm:gap-4">
                <dt className="text-sm font-medium text-secondary">{item.label}</dt>
                <dd className="text-sm leading-relaxed text-primary">{item.value}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}
      {!eventDetails?.length && detail && (
        <p className="mt-4 max-w-md rounded-lg bg-neutral px-4 py-3 text-sm leading-relaxed text-primary">
          {detail}
        </p>
      )}
      {(primaryAction || secondaryAction) && (
        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          {primaryAction && (
            <button type="button" className="btn" onClick={primaryAction.onClick}>
              {primaryAction.label}
            </button>
          )}
          {secondaryAction && (
            <button
              type="button"
              className="btn border-2 border-primary bg-white text-primary hover:bg-neutral"
              onClick={secondaryAction.onClick}
            >
              {secondaryAction.label}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
